import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { useMediaQuery } from "@mui/material";

import Button from "components/Button";
import ExtendedList from "components/List";
import ExtendedTable from "components/Table";
import EmptyBlock from "components/EmptyBlock";
import { MenuItem } from "components/ActionMenu/types";

import AccountFormModal from "../AccountFormModal";
import AccountDeleteModal from "../AccountDeleteModal";

import {
  accountColumnsDesktop,
  accountColumnsMobile,
} from "shared/constants/ibanColumns";

import { routes } from "routes/routes";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  IbanAccount,
  EditableIbanAccount,
  getIbanAccounts,
} from "store/features/ibanAccountSlice";

import { ReactComponent as DuplicateIcon } from "assets/images/actionMenu/duplicate-menu-icon.svg";
import { ReactComponent as CreateInvoiceIcon } from "assets/images/actionMenu/create-invoice-menu-icon.svg";
import { ReactComponent as EditIcon } from "assets/images/actionMenu/edit-menu-icon.svg";
import { ReactComponent as DeleteIcon } from "assets/images/actionMenu/delete-menu-icon.svg";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

const AccountsMain: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isAccountsLoading, setIsAccountsLoading] = useState(false);

  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [selectedAccount, setSelectedAccount] =
    useState<EditableIbanAccount | null>(null);

  const isXs = useMediaQuery("(max-width: 1040px)");

  const globalLoading = useAppSelector((state) => state.loading.isLoading);

  const accounts = useAppSelector((state) => state.ibanAccount.accounts);

  useEffect(() => {
    // Fetch accounts on tab click
    setIsAccountsLoading(true);
    dispatch(getIbanAccounts())
      .unwrap()
      .finally(() => {
        setIsAccountsLoading(false);
      });
  }, [dispatch]);

  const onCreateAccount = () => {
    analytics().sendEvent(AnalyticsEvents.accounts_create_account);
    setFormModalOpen(true);
  };

  const onUpdateAccount = (account?: IbanAccount) => {
    analytics().sendEvent(AnalyticsEvents.accounts_edit_account);

    setSelectedAccount(account || null);
    setFormModalOpen(true);
  };

  const onCreateInvoice = (account?: IbanAccount) => {
    analytics().sendEvent(AnalyticsEvents.accounts_create_invoice);

    navigate(`${routes.createInvoice}?accountUid=${account?.ibanAccountUid}`, {
      state: { from: routes.accounts },
    });
  };

  const onDuplicateAccount = (account?: IbanAccount) => {
    analytics().sendEvent(AnalyticsEvents.accounts_dublicate_account);

    setSelectedAccount(account ? { ...account, ibanAccountUid: null } : null);
    setFormModalOpen(true);
  };

  const onDeleteAccount = (account?: IbanAccount) => {
    analytics().sendEvent(AnalyticsEvents.accounts_delete_account);

    account && setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };

  const onCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedAccount(null);
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedAccount(null);
  };

  const tableActions: MenuItem<IbanAccount>[] = [
    {
      id: "create-invoice",
      name: "Створити посилання",
      icon: CreateInvoiceIcon,
      action: onCreateInvoice,
      hidden: (row: IbanAccount) => row.isSuspended,
    },
    {
      id: "dublicate",
      name: "Дублювати",
      icon: DuplicateIcon,
      action: onDuplicateAccount,
    },
    {
      id: "edit",
      name: "Редагувати",
      icon: EditIcon,
      action: onUpdateAccount,
    },
    {
      id: "delete",
      name: "Видалити",
      icon: DeleteIcon,
      action: onDeleteAccount,
    },
  ];

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        width="100%"
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography sx={{ mb: 0.5 }} variant="h3">
            Мої реквізити
          </Typography>
          <Typography variant="body1" color="grey700">
            Всі створені реквізити
          </Typography>
        </Box>
        <Button variant="contained" color="primary" onClick={onCreateAccount}>
          Створити реквізити
        </Button>
      </Box>
      {!globalLoading && accounts && accounts.length <= 0 ? (
        <EmptyBlock text="Ви ще не маєте жодних створених реквізитів." />
      ) : (
        <>
          {isXs ? (
            <ExtendedList<IbanAccount>
              loading={isAccountsLoading || globalLoading}
              heading={accountColumnsMobile}
              rows={accounts || []}
              actions={tableActions}
            />
          ) : (
            <ExtendedTable<IbanAccount>
              loading={isAccountsLoading || globalLoading}
              columns={accountColumnsDesktop}
              rows={accounts || []}
              actions={tableActions}
            />
          )}
        </>
      )}

      <AccountFormModal
        isOpen={isFormModalOpen}
        account={selectedAccount}
        onClose={onCloseFormModal}
      />
      <AccountDeleteModal
        isOpen={isDeleteModalOpen}
        account={selectedAccount}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};

export default AccountsMain;
